import React, { useState } from 'react';
import {
  Box,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddShoppingCart,
  ImageNotSupported,
} from '@mui/icons-material';
import { ModernProductCard, POS_PRIMARY, StockBadge } from './stylesProductPercabang';
import { getApiBaseUrl } from './apiProductPercabang';
import { ringkasGrosir } from '../../../utils/hargaGrosir';

/* ─── Skeleton ─── */
const ProductSkeleton = () => (
  <Box sx={{ border: '0.5px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
    <Skeleton variant="rectangular" sx={{aspectRatio: '16/9', width: '100%' }} />
    <Box sx={{ p: 1.25 }}>
      <Skeleton width="80%" height={18} />
      <Skeleton width="50%" height={14} sx={{ mt: 0.5 }} />
      <Skeleton width="60%" height={22} sx={{ mt: 1 }} />
    </Box>
  </Box>
);

export { ProductSkeleton };

/* ─── Main Component ─── */
export default function CardProductPercabang({ product, viewMode, onAdd, loading = false }) {
  const [imgError, setImgError] = useState(false);

  const stok = parseInt(product?.stok || 0, 10);
  const isHabis = isNaN(stok) || stok <= 0;
  const nama = product?.Barang?.namabarang || '-';
  const kode = product?.Barang?.kodebarang || '';
  const harga = parseFloat(product?.Barang?.harga || 0);
  const grosir = ringkasGrosir(product?.hargaGrosir);
  const kategori = product?.Barang?.Kategori?.namakategori || '';
  const fotoUrl = product?.Barang?.foto ? `${getApiBaseUrl()}/uploads/${product.Barang.foto}` : null;

  /* ════════════════════════════════
     VIEW MODE: LIST
  ════════════════════════════════ */
  if (viewMode === 'list') {
    return (
     
        <Box
          sx={{
            border: '0.5px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            minHeight: 96,
            opacity: isHabis ? 0.55 : 1,
            transition: 'box-shadow 0.15s, border-color 0.15s',
            cursor: isHabis ? 'default' : 'pointer',
            '&:hover': isHabis ? {} : {
              boxShadow: '0 2px 12px 0 rgba(0,0,0,0.09)',
              borderColor: 'primary.main',
            },
          }}
          onClick={() => !isHabis && onAdd(product)}
        >
          {/* Foto */}
          <Box
            sx={{
              width: 120,
              flexShrink: 0,
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {fotoUrl && !imgError ? (
              <Box
                component="img"
                src={fotoUrl}
                alt={nama}
                onError={() => setImgError(true)}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <ImageNotSupported sx={{ fontSize: 32, color: 'grey.400' }} />
            )}
            {isHabis && <StockBadge>Habis</StockBadge>}
          </Box>

          {/* Info */}
          <CardContent
            sx={{
              flex: 1,
              py: 1.5,
              pl: 2,
              pr: 1,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#172b4d' }}>
                {nama}
              </Typography>
              {!!kode && (
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {kode}
                </Typography>
              )}
              {!!kategori && (
                <Chip
                  label={kategori}
                  size="small"
                  sx={{ mt: 0.5, fontSize: 10, height: 20, bgcolor: 'grey.100' }}
                />
              )}
              {grosir && <Typography variant="caption" color="success.main" display="block" sx={{ mt: 0.5 }}>Grosir {grosir}</Typography>}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.75 }}>
                <Typography variant="body2" fontWeight={700} color="primary.main">
                  Rp {harga.toLocaleString('id-ID')}
                </Typography>
                <Typography
                  variant="caption"
                  color={stok <= 5 && stok > 0 ? 'warning.main' : 'text.secondary'}
                >
                  Stok: {stok}
                </Typography>
              </Box>
            </Box>

            <Tooltip title={isHabis ? 'Stok habis' : 'Tambah ke keranjang'} placement="top">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={isHabis || loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isHabis) onAdd(product);
                  }}
                  sx={{
                    width: 42,
                    height: 42,
                    flexShrink: 0,
                    border: '0.5px solid',
                    borderColor: 'primary.light',
                    borderRadius: 1.5,
                    '&:hover': { bgcolor: 'primary.50' },
                  }}
                >
                  {loading ? <CircularProgress size={16} /> : <AddShoppingCart sx={{ fontSize: 20 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </CardContent>
        </Box>
     
    );
  }

  /* ════════════════════════════════
     VIEW MODE: GRID (default)
  ════════════════════════════════ */
  return (
  
      <Box
        sx={{
          border: '0.5px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          opacity: isHabis ? 0.55 : 1,
          transition: 'box-shadow 0.15s, border-color 0.15s',
          cursor: isHabis ? 'default' : 'pointer',
          '&:hover': isHabis ? {} : {
            boxShadow: '0 2px 12px 0 rgba(0,0,0,0.09)',
            borderColor: 'primary.main',
          },
        }}
        onClick={() => !isHabis && onAdd(product)}
      >
        {/* Foto */}
        <Box
          sx={{
            width: '100%',
            aspectRatio: '16/9',
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {fotoUrl && !imgError ? (
            <Box
              component="img"
              src={fotoUrl}
              alt={nama}
              onError={() => setImgError(true)}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <ImageNotSupported sx={{ fontSize: 36, color: 'grey.400' }} />
          )}

          {/* Badge stok habis */}
          {isHabis && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Chip label="Stok Habis" size="small" color="error" />
            </Box>
          )}

          {/* Badge stok menipis */}
          {!isHabis && stok <= 10 && (
            <Chip
              label={`Stok ${stok}`}
              size="small"
              color="warning"
              sx={{ position: 'absolute', top: 6, right: 6, fontSize: 10, height: 20 }}
            />
          )}

          {/* Badge kategori */}
          {!!kategori && (
            <Chip
              label={kategori}
              size="small"
              sx={{
                position: 'absolute',
                top: 6,
                left: 6,
                fontSize: 10,
                height: 20,
                bgcolor: 'rgba(255,255,255,0.88)',
                color: 'text.secondary',
                border: '0.5px solid',
                borderColor: 'divider',
              }}
            />
          )}
        </Box>

        {/* Info produk */}
        <Box sx={{ p: 1.25, display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
          <Typography
            variant="body2"
            fontWeight={500}
            sx={{
              lineHeight: 1.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {nama}
          </Typography>

          {!!kode && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', letterSpacing: 0.3 }}>
              {kode}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: 0.75 }}>
            <Box>
              <Typography variant="body2" fontWeight={700} color="primary.main">
                Rp {harga.toLocaleString('id-ID')}
              </Typography>
              {grosir && <Typography variant="caption" color="success.main" display="block">Grosir {grosir}</Typography>}
              <Typography variant="caption" color={stok <= 5 && stok > 0 ? 'warning.main' : 'text.secondary'}>
                Stok: {stok}
              </Typography>
            </Box>

            <Tooltip title={isHabis ? 'Stok habis' : 'Tambah ke keranjang'} placement="top">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={isHabis || loading}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isHabis) onAdd(product);
                  }}
                  sx={{
                    border: '0.5px solid',
                    borderColor: 'primary.light',
                    borderRadius: 1.5,
                    '&:hover': { bgcolor: 'primary.50' },
                  }}
                >
                  {loading ? <CircularProgress size={16} /> : <AddShoppingCart sx={{ fontSize: 18} } />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>
   
  );
}